import { apiRequest } from './http';
import type {
  AccountLedger,
  Journal,
  JournalPage,
  JournalQuery,
  JournalType,
  JournalValidation,
  TrialBalance,
} from './types';

/**
 * One line as the General Ledger accepts it: an account, exactly one positive side, and the counterparty on
 * the accounts that are analysed by person.
 */
export interface JournalLineInput {
  accountId: string;
  debitAmount: number;
  creditAmount: number;
  description: string;
  personId: string | null;
}

/** The number is absent by design: a manual journal takes the next one in the tenant's own series. */
export interface CreateJournalInput {
  description: string;
  postingDate: string;
  currencyCode: string;
  type: JournalType;
  lines: readonly JournalLineInput[];
}

export interface UpdateJournalInput extends CreateJournalInput {
  expectedVersion: number;
}

/**
 * Posting and reversal are the two operations a retry must never repeat, so each carries a key the server
 * files the result under. A retried request returns the original outcome instead of a second posting.
 */
function idempotencyKey(operation: string, journalId: string): string {
  const nonce = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}`;
  return `${operation}-${journalId}-${nonce}`.slice(0, 128);
}

function withIdempotency(key: string, body?: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Idempotency-Key': key },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  };
}

export function journalQueryString(query: JournalQuery): string {
  const params = new URLSearchParams();
  if (query.search) params.set('q', query.search);
  if (query.states?.length) params.set('status', query.states.join(','));
  if (query.types?.length) params.set('type', query.types.join(','));
  if (query.fromDate) params.set('fromDate', query.fromDate);
  if (query.toDate) params.set('toDate', query.toDate);
  if (query.sourceModule) params.set('sourceModule', query.sourceModule);
  if (query.sort) params.set('sort', query.sort);
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  return params.size ? `?${params.toString()}` : '';
}

export const journalsApi = {
  list: (query: JournalQuery = {}) => apiRequest<JournalPage>(`/journals${journalQueryString(query)}`),

  get: (journalId: string) => apiRequest<Journal>(`/journals/${journalId}`),

  create: (input: CreateJournalInput) =>
    apiRequest<Journal>('/journals', { method: 'POST', body: JSON.stringify(input) }),

  /** Replaces a draft outright. The version the browser read decides whether the write is still valid. */
  update: (journalId: string, input: UpdateJournalInput) =>
    apiRequest<Journal>(`/journals/${journalId}`, { method: 'PUT', body: JSON.stringify(input) }),

  remove: (journalId: string, expectedVersion: number) =>
    apiRequest<void>(`/journals/${journalId}?expectedVersion=${expectedVersion}`, { method: 'DELETE' }),

  /** Asks the server what it would reject, without moving the journal anywhere. */
  validate: (journalId: string) =>
    apiRequest<JournalValidation>(`/journals/${journalId}/validate`, { method: 'POST' }),

  submit: (journalId: string) => apiRequest<Journal>(`/journals/${journalId}/submit`, { method: 'POST' }),

  approve: (journalId: string) => apiRequest<Journal>(`/journals/${journalId}/approve`, { method: 'POST' }),

  reject: (journalId: string, reason: string) =>
    apiRequest<Journal>(`/journals/${journalId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),

  post: (journalId: string) =>
    apiRequest<Journal>(`/journals/${journalId}/post`, withIdempotency(idempotencyKey('post', journalId))),

  /** Returns the reversal, which is a new posted journal rather than a change to the original. */
  reverse: (journalId: string, postingDate: string, reason: string) =>
    apiRequest<Journal>(
      `/journals/${journalId}/reverse`,
      withIdempotency(idempotencyKey('reverse', journalId), { postingDate, reason }),
    ),

  /** One account's own ledger page. An open window reports every entry ever posted to it. */
  accountLedger: (accountId: string, fromDate?: string, toDate?: string, includeDescendants = false) => {
    const params = new URLSearchParams();
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    if (includeDescendants) params.set('includeDescendants', 'true');
    const suffix = params.size ? `?${params.toString()}` : '';
    return apiRequest<AccountLedger>(`/accounts/${accountId}/ledger${suffix}`);
  },

  trialBalance: (fromDate?: string, toDate?: string) => {
    const params = new URLSearchParams();
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    const suffix = params.size ? `?${params.toString()}` : '';
    return apiRequest<TrialBalance>(`/trial-balance${suffix}`);
  },
};
